<script setup>
import AttachmentFiles from "../../Components/AttachmentFiles.vue";
import FullCalendar from '@fullcalendar/vue3'
import frLocale from '@fullcalendar/core/locales/fr';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {useHttpErrorsHandler} from "../../Composables/httpErrorsHandler.js";
import {useRouter} from "vue-router";
import {computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import Loader from "../../Components/Loader.vue";
import {useToast} from "vue-toast-notification";
import Chat from "../../Components/Chat.vue";
import FormTextarea from "../../Components/FormTextarea.vue";
import FormMultiSelect from "../../Components/FormMultiSelect.vue";
import CopyButton from "../../Components/CopyButton.vue";
import axios from "axios";
import {userIsStaff, userIsExterne, userIsAdmin, userIsBackOffice} from '../../Utils/userRoleUtils.js'
import { DocusealForm } from '@docuseal/vue'
import FormSelectField from "../../Components/FormSelectField.vue";
import FormSwitch from "../../Components/FormSwitch.vue";
import { DEFAULT_APPOINTMENT_DURATION_MINUTES, STAGE, findAppointmentType } from "../../Constants/appointmentTypes.js";
import FormDateTimePicker from "../../Components/FormDateTimePicker.vue";
import FormTextField from "../../Components/FormTextField.vue";
import FormNumericField from "../../Components/FormNumericField.vue";
import FormDatePicker from "../../Components/FormDatePicker.vue";
import GoogleCalendarColorSelector from "../../Components/GoogleCalendarColorSelector.vue";
import Modal from "../../Components/Modal.vue";
import {getHolidaysAsEvents} from "../../Utils/holidays.js";

const $toast = useToast();
const { httpErrorsHandler } = useHttpErrorsHandler();
const router = useRouter();
const clientRequestId = router.currentRoute.value.params.id;
const loading = ref(true)
const errors = ref(null)
const clientRequest = ref(null)
const prestationPreSelected = ref([])
function getClientRequest(clientRequestId) {
    loading.value = true;
    axios.get("/api/client-requests/" + clientRequestId)
        .then(async response => {
            clientRequest.value = response.data;
            internalNotes.value = response.data.data.notes;
            loading.value = false;
            //Populate the configurator
            if (response.data.data.vehicle_type !== null) {
                formClientRequestUpdate.vehicle_type = response.data.data.vehicle_type;
                await getCarBrands();
            }
            if (response.data.data.vehicle_brand_id !== null) {
                formClientRequestUpdate.vehicle_brand_id = response.data.data.vehicle_brand_id;
                await getCarModels();
            }
            if (response.data.data.vehicle_model_id !== null) {
                formClientRequestUpdate.vehicle_model_id = response.data.data.vehicle_model_id;
                await getCarVersions();
            }
            if (response.data.data.vehicle_version_id !== null) {
                formClientRequestUpdate.vehicle_version_id = response.data.data.vehicle_version_id;
                formClientRequestUpdate.vehicle_engine_id = response.data.data.vehicle_engine_id;
                await getCarEngines();
            }
            if (response.data.data.appointment !== null) {
                formAppointment.start_date = response.data.data.appointment.data.start_date_en;
                formAppointment.end_date = response.data.data.appointment.data.end_date_en;
                preselectedColor.value = response.data.data.appointment.data.google_color_id;
                formAppointment.google_color_id = response.data.data.appointment.data.google_color_id;
            }
            Object.entries(response.data.data).forEach(([key, value]) => {
                if (key === 'tags') {
                    tagsPreSelected.value = value;
                    return;
                }
                if (Object.hasOwn(formClientRequestUpdate, key) && key !== 'address') {
                    formClientRequestUpdate[key] = value;
                }
            });
            if (response.data.data.car_stage !== null) {
                prestationPreSelected.value = mapCarStageToPrestation(response.data.data.car_stage);
                if(preselectedColor.value === null){
                    preselectedColor.value = mapCarStageToGoogleColorId(response.data.data.car_stage);
                }
            }
            resetNotificationNumber(clientRequestId);
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

onBeforeMount(() => {
    getClientRequest(clientRequestId)
})

function changeStatus(status) {
    if (status === clientRequest.value.data.status) {
        return;
    }
    if (status === 'lost') {
        return showModalReasonForLoss();
    }
    axios.patch("/api/client-requests/" + clientRequest.value.data.id + "/status", { status: status })
        .then(response => {
            clientRequest.value = response.data;
            formClientRequestUpdate.status = response.data.data.status;
            $toast.success('Statut mis à jour', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

function resetNotificationNumber(clientRequestId) {
    if (clientRequest.value?.data?.notifications_center > 0){
        axios.patch("/api/client-requests/" + clientRequestId + "/reset-notification-number")
            .then(() => {
                if (clientRequest.value?.data) {
                    clientRequest.value.data.notifications_center = 0;
                }
            })
            .catch(error => {
                httpErrorsHandler(error);
            });
    }
}

//Hide modal if leave page
onBeforeUnmount(() => {
    Echo.leave(`client_requests.${clientRequestId}`);
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement);
        if (modalInstance) {
            modalInstance.hide();
        }
    });
})

const clientRequestStatuses = reactive([
    { value: 'pending', label: 'À traiter', badgeColor: 'danger' },
    { value: 'responded', label: 'Répondu', badgeColor: 'secondary' },
    { value: 'parts_ordered', label: 'Pièces commandées', badgeColor: 'info' },
    { value: 'appointment_scheduled', label: 'RDV programmé', badgeColor: 'success' },
    { value: 'appointment_ongoing', label: 'RDV en cours', badgeColor: 'success' },
    { value: 'won', label: 'Gagné', badgeColor: 'success' },
    { value: 'no_show', label: 'Client non présenté', badgeColor: 'dark' },
    { value: 'lost', label: 'Perdu', badgeColor: 'dark' },
    { value: 'closed', label: 'Fermé', badgeColor: 'dark' },
]);

function showModalInternalNotes() {
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalInternalNotes'))
    modal.show();
}
const internalNotes = ref(null)
function updateInternalNotes() {
    axios.patch("/api/client-requests/" + clientRequest.value.data.id + "/notes", { notes: internalNotes.value })
        .then(response => {
            clientRequest.value.data.notes = response.data.data.notes;
            $toast.success('Note interne mise à jour', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalInternalNotes'))
            modal.hide();
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

const formPdf = reactive({
    "prestations": [],
    "options": [],
})
const generatingPdf = ref(false)
function generatePdf() {
    generatingPdf.value = true;
    let formData = new FormData();
    Object.entries(formPdf).forEach(([key, value]) => {
        return value.forEach((item) => {
            formData.append(`${key}[]`, item.id);
        });
    });

    axios.post("/api/client-requests/" + clientRequest.value.data.id + "/pdf", formData)
        .then(response => {
            $toast.success('Fiche de travail générée', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            clientRequest.value.data.worksheet_path = response.data.data.worksheet_path;
            clientRequest.value.data.worksheet_generated_at = response.data.data.worksheet_generated_at;
            generatingPdf.value = false;
            window.open(response.data.data.worksheet_path, '_blank');
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalPdf'))
            modal.hide();
            formPdf.prestations = [];
            formPdf.options = [];
        })
        .catch(error => {
            httpErrorsHandler(error);
            generatingPdf.value = false;
        });
}

function showModalPdf() {
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalPdf'))
    modal.show();
}

function canGeneratePdf() {
    if (clientRequest.value.data.vehicle_brand_id === null
        || clientRequest.value.data.vehicle_model_id === null
        || clientRequest.value.data.vehicle_version_id === null
        || clientRequest.value.data.vehicle_engine_id === null
        || clientRequest.value.data.vin === null
        || clientRequest.value.data.license_plate === null
        || clientRequest.value.data.vehicle_year === null
        || clientRequest.value.data.price === null
        || clientRequest.value.data.mileage === null) {
        return false;
    }

    if (clientRequest.value.data.vehicle_power_type === 'petrol' || clientRequest.value.data.vehicle_power_type === 'hybrid_petrol') {
        if (clientRequest.value.data.spark_plug_mileage === null
            || clientRequest.value.data.coil_mileage === null
            || clientRequest.value.data.octane_rating === null) {
            return false;
        }
    }

    return true;
}

const slugForm = ref(null)
const loadingSignature = ref(false)
function generateSubmission() {
    loadingSignature.value = true;
    axios.get("/api/client-requests/" + clientRequest.value.data.id + "/initiate-signature")
        .then(response => {
            //close modal pdf
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalPdf'))
            modal.hide();
            const modalSignature = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalSignature'))
            modalSignature.show();
            slugForm.value = response.data.slug;
            loadingSignature.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
            loadingSignature.value = false;
        });
}

function signatureCompleted() {
    const modalSignature = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalSignature'))
    modalSignature.hide();
    $toast.success('Fiche de travail signée. Un email avec la fiche va être envoyé.', {
        position: 'top',
        duration: 5000,
        dismissible: true,
        pauseOnHover: true,
    });
}

onMounted(() => {
    Echo.private(`client_requests.${clientRequestId}`)
        .listen('ClientRequestSignedEvent', (e) => {
            if (clientRequest.value?.data) {
                clientRequest.value.data.worksheet_signed_path = e.data.data.worksheet_signed_path;
                clientRequest.value.data.worksheet_audit_path = e.data.data.worksheet_audit_path;
            }
            $toast.info('Documents signés disponibles en téléchargement', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
        });
});

const showOriginReset = ref(false)
watch(() => formPdf.prestations, () => {
    showOriginReset.value = formPdf.prestations.some(option => option.id === 'ORIGIN_RESET');
})

const formReasonForLoss = reactive({
    "reason_for_loss": null,
})

function showModalReasonForLoss() {
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalReasonForLoss'))
    modal.show();
}

const loadingReasonForLoss = ref(false)
function updateReasonForLoss() {
    loadingReasonForLoss.value = true;
    axios.patch("/api/client-requests/" + clientRequest.value.data.id + "/reason-for-loss", { reason_for_loss: formReasonForLoss.reason_for_loss })
        .then(response => {
            clientRequest.value = response.data;
            $toast.success('Statut et raison de la perte mise à jour', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalReasonForLoss'))
            modal.hide();
            formReasonForLoss.reason_for_loss = null;
            loadingReasonForLoss.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

const loadingClientRequestUpdate = ref(false)
const formClientRequestUpdate = reactive({
    client_id: null,
    center_id: null,
    status: null,
    reason_for_loss: null,
    tags: [],
    vehicle_brand_id: null,
    vehicle_model_id: null,
    vehicle_version_id: null,
    vehicle_engine_id: null,
    car_stage: null,
    contact_reason: null,
    vehicle_type: null,
    notes: null,
    request_origin: null,
    vin: null,
    license_plate: null,
    vehicle_year: null,
    mileage: null,
    spark_plug_mileage: null,
    coil_mileage: null,
    octane_rating: null,
    price: null,
    currency: null
})
const tagsPreSelected = ref([])
const isInitialized = ref(true)
const carBrands = ref([]);
const carModels = ref([]);
const carVersions = ref([]);
const carEngines = ref([]);
const showEngineDetails = ref(false);
watch(
    () => formClientRequestUpdate.vehicle_type,
    async (newValues, oldValues) => {
        if (isInitialized.value && newValues !== oldValues) {
            formClientRequestUpdate.vehicle_brand_id = null;
            formClientRequestUpdate.vehicle_model_id = null;
            formClientRequestUpdate.vehicle_version_id = null;
            formClientRequestUpdate.vehicle_engine_id = null;
            carBrands.value = [];
            if (formClientRequestUpdate.vehicle_type !== null){
                await getCarBrands();
            }
        }
    },
    { deep: true }
);

function getCarBrands(){
    carBrands.value = [];
    return new Promise((resolve, reject) => {
        axios.get('/api/chiptuning/brands?type=' + formClientRequestUpdate.vehicle_type)
            .then(response => {
                response.data.data.forEach(brand => {
                    carBrands.value.push({
                        text: brand.data.name,
                        value: brand.data.id
                    })
                })
                resolve();
            })
            .catch(error => {
                httpErrorsHandler(error);
                reject(error);
            });
    });
}

watch(
    () => formClientRequestUpdate.vehicle_brand_id,
    async (newValues, oldValues) => {
        if (isInitialized.value && newValues !== oldValues) {
            formClientRequestUpdate.vehicle_model_id = null;
            formClientRequestUpdate.vehicle_version_id = null;
            formClientRequestUpdate.vehicle_engine_id = null;
            carModels.value = [];
            if (formClientRequestUpdate.vehicle_brand_id !== null) {
                await getCarModels();
            }
        }
    },
    { deep: true }
);

function getCarModels(){
    carModels.value = [];
    return new Promise((resolve, reject) => {
        axios.get('/api/chiptuning/models?vehicle_brand_id=' + formClientRequestUpdate.vehicle_brand_id)
            .then(response => {
                response.data.data.forEach(model => {
                    carModels.value.push({
                        text: model.data.name,
                        value: model.data.id
                    })
                })
                resolve();
            })
            .catch(error => {
                httpErrorsHandler(error);
                reject(error);
            });
    });
}

watch(
    () => formClientRequestUpdate.vehicle_model_id,
    async (newValues, oldValues) => {
        if (isInitialized.value && newValues !== oldValues) {
            formClientRequestUpdate.vehicle_version_id = null;
            formClientRequestUpdate.vehicle_engine_id = null;
            carVersions.value = [];
            if (formClientRequestUpdate.vehicle_model_id !== null){
                await getCarVersions();
            }
        }
    },
    { deep: true }
);
function getCarVersions(){
    carVersions.value = [];
    return new Promise((resolve, reject) => {
        axios.get('/api/chiptuning/versions?vehicle_model_id=' + formClientRequestUpdate.vehicle_model_id)
            .then(response => {
                response.data.data.forEach(model => {
                    carVersions.value.push({
                        text: model.data.name,
                        value: model.data.id
                    })
                })
                resolve();
            })
            .catch(error => {
                httpErrorsHandler(error);
                reject(error);
            });
    });
}

watch(
    () => formClientRequestUpdate.vehicle_version_id,
    async (newValues, oldValues) => {
        if (isInitialized.value && newValues !== oldValues) {
            carEngines.value = [];
            formClientRequestUpdate.vehicle_engine_id = null;
            if (formClientRequestUpdate.vehicle_version_id !== null){
                await getCarEngines();
            }
        }
    },
    { deep: true }
);

function getCarEngines(){
    carEngines.value = [];
    return new Promise((resolve, reject) => {
        axios.get('/api/chiptuning?all=1&vehicle_version_id=' + formClientRequestUpdate.vehicle_version_id)
            .then(response => {
                response.data.data.forEach(model => {
                    carEngines.value.push({
                        text: model.data.power_type_label + ' - ' + model.data.name + ' - ' + model.data.horsepower + 'ch / ' + model.data.torque + 'Nm',
                        value: model.data.id
                    })
                })
                resolve();
            })
            .catch(error => {
                httpErrorsHandler(error);
                reject(error);
            });
    });
}

watch(
    () => formClientRequestUpdate.vehicle_engine_id,
    async (newValues, oldValues) => {
        if (isInitialized.value && newValues !== oldValues) {
            if (showEngineDetails.value === true){
                showEngineDetails.value = false;
            }
        }
    },
    { deep: true }
);

function showModalClientRequestUpdate() {
    // Toujours le statut actuel : un statut lu au chargement de la page écrasait « RDV programmé »
    formClientRequestUpdate.status = clientRequest.value.data.status;
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalUpdateClientRequest'))
    modal.show();
}

function updateClientRequest() {

    loadingClientRequestUpdate.value = true;
    let formData = new FormData();

    Object.entries(formClientRequestUpdate).forEach(([key, value]) => {
        if (typeof value === 'boolean') {
            return formData.append(key, value ? 1 : 0);
        }
        if (key === 'tags') {
            return value.forEach((tag) => {
                formData.append(`${key}[]`, tag.id);
            });
        }
        if (value !== null && value !== '') {
            formData.append(key, value);
        }
    });

    formData.append('_method', 'PUT');
    axios.post("/api/client-requests/" + clientRequest.value.data.id, formData)
        .then(response => {
            clientRequest.value = response.data;
            $toast.success('Demande client modifiés avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            loadingClientRequestUpdate.value = false;
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalUpdateClientRequest'))
            modal.hide();
            Object.entries(response.data.data).forEach(([key, value]) => {
                if (key === 'tags') {
                    tagsPreSelected.value = value;
                }
            });
        })
        .catch(error => {
            errors.value = error.response.data.errors;
            httpErrorsHandler(error);
            loadingClientRequestUpdate.value = false;
        });
}

const loadingCalendar = ref(true)
const displayCalendar = ref(false);
function showModalCalendar(event, modalId) {
    eventToShow.value = event;
    const modalElement = document.getElementById(modalId);
    const modalCalendar = bootstrap.Modal.getOrCreateInstance(modalElement);

    const showCalendar = () => {
        displayCalendar.value = true;
        loadingCalendar.value = false;
        // RDV déjà fixé : l'agenda s'ouvre sur son jour
        nextTick(() => {
            if (formAppointment.start_date && calendarRef.value) {
                calendarRef.value.getApi().gotoDate(parseDate(formAppointment.start_date));
            }
        });
    };

    const onModalShown = function (e) {
        if (e.target.id === modalId) {
            showCalendar();
            document.removeEventListener('shown.bs.modal', onModalShown);
        }
    };
    document.addEventListener('shown.bs.modal', onModalShown);
    modalCalendar.show();
}


const calendarRef = ref(null)
// Numéro du dernier chargement : une réponse plus ancienne arrivée en retard est ignorée
let lastEventsRequestId = 0;

function getEvents(startDate = null, endDate = null) {
    const eventsRequestId = ++lastEventsRequestId;
    startDateSave.value = startDate;
    endDateSave.value = endDate;

    const start = new Date(startDate);
    start.setDate(start.getDate() - 1);
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1);

    axios.get('/api/events', { params: {
            start_date: start,
            end_date: end,
            show_appointments_events: true,
            centers_id: [clientRequest.value.data.center_id]
        } })
        .then(response => {
            if (eventsRequestId !== lastEventsRequestId) return;
            events.value.splice(0, events.value.length);

            // Ajouter les jours fériés
            const holidays = getHolidaysAsEvents(start, end);
            events.value.push(...holidays);

            response.data.data.forEach(event => {
                events.value.push({
                    id: event.data.id,
                    title: event.data.title,
                    start: event.data.start_date,
                    end: event.data.all_day ? event.data.end_date_all_day : event.data.end_date,
                    backgroundColor: event.data.color ?? '#212529',
                    allDay: event.data.all_day,
                    location: event.data.location,
                    data : event.data,
                    can : event.can,
                    center: event.data.center_id === null ? null : event.data.center.data.name
                });
            });
            addOrUpdateCurrentEvent();
            loading.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

function addOrUpdateCurrentEvent() {
    const idEvent = clientRequest.value.data.appointment === null ? clientRequest.value.data.id : clientRequest.value.data.appointment.data.id;
    const index = events.value.findIndex(e => e.id === idEvent);
    if (index !== -1) {
        events.value.splice(index, 1);
    }
    if (formAppointment.start_date && formAppointment.end_date) {
        events.value.push({
            id: idEvent,
            title: appointmentTitle(),
            start: parseDate(formAppointment.start_date),
            end: parseDate(formAppointment.end_date),
            allDay: false,
            backgroundColor: getColorFromGoogleCalendarId(formAppointment.google_color_id),
            // Seul le RDV en cours se déplace et s'étire à la souris
            editable: true,
            classNames: ['fc-current-appointment'],
        });
    }
}

const formAppointment = reactive({
    start_date: null,
    end_date: null,
    google_color_id: 7,
    title: null,
})

// Titre du RDV enregistré (mis à jour aussi quand la demande change de véhicule) ; vide = titre par défaut
watch(() => clientRequest.value?.data?.appointment?.data?.title ?? null, (title) => {
    formAppointment.title = title;
}, { immediate: true })

const appointmentTitle = () => formAppointment.title?.trim() || getEventTitle()

// Prestations d'une demande (mêmes valeurs que la fenêtre « Modifier la demande »)
const PRESTATION_OPTIONS = [
    'Reprogrammation stage 0', 'Reprogrammation stage 1', 'Reprogrammation stage 2', 'Reprogrammation stage 3',
    'Boîte de vitesses', 'Conversion E85', 'Conversion E85+', 'Pièces performance', 'Entretien',
    'Passage au banc', 'Esthétique', 'Diagnostic',
]
const PRESTATION_SELECT_OPTIONS = PRESTATION_OPTIONS.map((option) => ({ text: option, value: option }))
const STAGES = ['Reprogrammation stage 1', 'Reprogrammation stage 2', 'Reprogrammation stage 3']

/**
 * Prestation qui correspond à un type de RDV (réglée dans Constants/appointmentTypes.js).
 * null = pas d'équivalent : rien n'est proposé.
 */
function prestationForAppointmentType(googleColorId, currentCarStage) {
    const type = findAppointmentType(googleColorId)
    if (!type || type.prestation === null) return null
    if (type.prestation === STAGE) return STAGES.includes(currentCarStage) ? currentCarStage : 'Reprogrammation stage 1'
    return type.prestation
}

// Durée par défaut d'un type de RDV, en minutes
const appointmentDurationMinutes = (googleColorId) => findAppointmentType(googleColorId)?.durationMinutes ?? DEFAULT_APPOINTMENT_DURATION_MINUTES

const getAppointmentTypeLabel = (googleColorId) => findAppointmentType(googleColorId)?.name ?? 'inconnu'

// Le type de RDV ne correspond plus à la prestation de la demande : on propose de la mettre à jour,
// sinon HubSpot enverrait au client une confirmation avec l'ancienne prestation
const prestationUpdate = reactive({ enabled: true, value: null })
const suggestedPrestation = computed(() => prestationForAppointmentType(formAppointment.google_color_id, clientRequest.value?.data?.car_stage ?? null))
const prestationMismatch = computed(() => suggestedPrestation.value !== null && suggestedPrestation.value !== (clientRequest.value?.data?.car_stage ?? null))

watch(suggestedPrestation, (value) => {
    prestationUpdate.value = value
    prestationUpdate.enabled = true
}, { immediate: true })
const events = ref([]);
const preselectedColor = ref(null);
const startDateSave =  ref(null);
const endDateSave = ref(null);
const eventToShow = ref(null);
const calendarOptions = {
    timeZone: 'local',
    themeSystem: 'bootstrap',
    initialView: 'timeGridThreeDay',
    plugins: [dayGridPlugin, listPlugin, timeGridPlugin, interactionPlugin],
    weekends: true,
    firstDay: 1,
    headerToolbar: {
        start: 'today prev,next',
        center: 'title',
        end: 'timeGridWeek,timeGridThreeDay,timeGridDay,listMonth'
    },
    eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    },
    events: events.value,
    expandRows: true,
    displayEventTime: true,
    locale: frLocale,
    eventColor: '#ffffff',
    scrollTime: '08:00:00',
    height: '100%',
    slotEventOverlap: false,
    // Choix de l'horaire dans l'agenda : clic sur un créneau (fin = durée du type) ou glisser sur plusieurs créneaux
    selectable: true,
    selectMirror: true,
    selectAllow: (selectInfo) => !selectInfo.allDay,
    select: (selectInfo) => {
        const isSingleSlotClick = selectInfo.end - selectInfo.start <= 30 * 60 * 1000;
        const endDate = new Date(selectInfo.start);
        endDate.setMinutes(endDate.getMinutes() + appointmentDurationMinutes(formAppointment.google_color_id));
        setAppointmentDatesFromCalendar(selectInfo.start, isSingleSlotClick ? endDate : selectInfo.end);
        selectInfo.view.calendar.unselect();
    },
    eventAllow: (dropInfo) => !dropInfo.allDay,
    eventDrop: (dropInfo) => setAppointmentDatesFromCalendar(dropInfo.event.start, dropInfo.event.end),
    eventResize: (resizeInfo) => setAppointmentDatesFromCalendar(resizeInfo.event.start, resizeInfo.event.end),
    allDaySlot : true,
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00',
    datesSet: async (dateInfo) => {
        getEvents(dateInfo.startStr, dateInfo.endStr);
    },
    eventContent: function(arg) {
        // RDV en cours et zone en train d'être sélectionnée : horaire + titre du RDV
        if (arg.view.type !== 'listMonth' && (arg.isMirror || arg.event.classNames.includes('fc-current-appointment'))) {
            return currentAppointmentContent(arg);
        }
        let titleWithCenter = arg.event.extendedProps.center ? arg.event.title + ' - ' + arg.event.extendedProps.center : arg.event.title;

        if (arg.view.type === 'listMonth') {
            return { html: titleWithCenter };
        } else {
            return { html: arg.event.title };
        }
    },
    eventDidMount: function(arg) {
        if (arg.view.type === 'listMonth') {
            const color = arg.event.backgroundColor || '#212529';
            const dot = arg.el.querySelector('.fc-list-event-dot');
            if (dot) {
                dot.style.borderColor = color;
                dot.style.backgroundColor = color;
            }
        }
    },
    views: {
        timeGridThreeDay: {
            type: 'timeGrid',
            duration: { days: 3 },
            buttonText: '3 Jours'
        },
    }
}

// Début fixé depuis l'agenda : la vue et la fin choisies dans l'agenda sont conservées
let startDateSetFromCalendar = false;

function setAppointmentDatesFromCalendar(startDate, endDate) {
    const newStartDate = formatDate(startDate, true);
    if (newStartDate !== formAppointment.start_date) {
        startDateSetFromCalendar = true;
        formAppointment.start_date = newStartDate;
    }
    formAppointment.end_date = formatDate(endDate, true);
    addOrUpdateCurrentEvent();
}

function currentAppointmentContent(arg) {
    const time = document.createElement('div');
    time.className = 'fw-semibold';
    time.textContent = arg.timeText;
    const title = document.createElement('div');
    title.textContent = arg.event.title || appointmentTitle();
    return { domNodes: [time, title] };
}

watch(() => formAppointment.start_date,
    (newStartDate) => {
        if (startDateSetFromCalendar) {
            startDateSetFromCalendar = false;
            return;
        }
        if (!newStartDate) return;
        if (calendarRef.value === null) return;

        const calendarApi = calendarRef.value.getApi();
        calendarApi.gotoDate(newStartDate);

        applyDefaultEndDate();
    }
);

// Fin du RDV = début + durée par défaut du type (modifiable ensuite à la main)
function applyDefaultEndDate() {
    if (!formAppointment.start_date) return;
    const endDate = parseDate(formAppointment.start_date);
    endDate.setMinutes(endDate.getMinutes() + appointmentDurationMinutes(formAppointment.google_color_id));
    formAppointment.end_date = formatDate(endDate, true);
}

watch(() => formAppointment.end_date,
    (newEndDate) => {
        if (!newEndDate) return;
        if (calendarRef.value === null) return;
        const startDate = parseDate(formAppointment.start_date);
        const endDate = parseDate(newEndDate);

        if (startDate >= endDate) {
            endDate.setHours(startDate.getHours() + 1);
            formAppointment.end_date = formatDate(endDate, true);
            $toast.warning('La date de fin ne peut pas être antérieure à la date de début.', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
        }
        addOrUpdateCurrentEvent();
    }
);
watch(() => formAppointment.title, () => {
    if (calendarRef.value === null) return;
    addOrUpdateCurrentEvent();
});
watch(() => formAppointment.google_color_id,
    (newType, oldType) => {
        if (calendarRef.value === null) return;
        // Changement de type par l'utilisateur : la fin suit la durée du nouveau type
        if (oldType !== undefined && String(newType) !== String(oldType)) {
            applyDefaultEndDate();
        }
        addOrUpdateCurrentEvent();
    }
);
function parseDate(dateStr) {
    const parts = dateStr.split(' ');
    const datePart = parts[0];
    const timePart = parts.length > 1 ? parts[1] : null;

    const [year, month, day] = datePart.split('-');
    if (timePart) {
        const [hours, minutes] = timePart.split(':');
        return new Date(year, month - 1, day, hours, minutes);
    } else {
        return new Date(year, month - 1, day);
    }
}

function formatDate(date, includeTime = true) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    if (includeTime) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    } else {
        return `${year}-${month}-${day}`;
    }
}

const loadingSetAppointment = ref(false)
function setAppointmentDate() {
    loadingSetAppointment.value = true;
    const payload = { ...formAppointment }
    if (prestationMismatch.value && prestationUpdate.enabled && prestationUpdate.value) {
        payload.car_stage = prestationUpdate.value
    }
    axios.patch("/api/client-requests/" + clientRequest.value.data.id + "/appointment", payload).
        then(response => {
            clientRequest.value.data.appointment = response.data.data.appointment;
            clientRequest.value.data.car_stage = response.data.data.car_stage;
            formClientRequestUpdate.car_stage = response.data.data.car_stage;
            prestationPreSelected.value = mapCarStageToPrestation(response.data.data.car_stage);
            clientRequest.value.data.status = response.data.data.status;
            formClientRequestUpdate.status = response.data.data.status;
            clientRequest.value.data.status_label_fr = response.data.data.status_label_fr;
            clientRequest.value.data.status_badge_color = response.data.data.status_badge_color;
            $toast.success('RDV mis à jour', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalCalendar'))
            modal.hide();
        })
        .catch(error => {
            errors.value = error.response?.data?.errors ?? {};
            httpErrorsHandler(error);
        })
        // Succès ou erreur (ex. dates manquantes) : le bouton redevient cliquable
        .finally(() => {
            loadingSetAppointment.value = false;
        });
}

function getColorFromGoogleCalendarId(colorId) {
    const colors = {
        1: '#7986CB',
        2: '#33B679',
        3: '#8E24AA',
        4: '#E67C73',
        5: '#F6BF26',
        6: '#F4511E',
        7: '#039BE5',
        8: '#616161',
        9: '#3F51B5',
        10: '#0B8043',
        11: '#D50000',
        12: '#7B3F00',
    };

    return colors[colorId] || '#7986CB';
}

function getEventTitle() {
    let title = '';
    if (clientRequest.value.data.vehicle_engine_id !== null) {
        title = clientRequest.value.data.vehicle_brand_name + ' ' + clientRequest.value.data.vehicle_model_name + ' - ' + clientRequest.value.data.vehicle_version_name + ' ' + clientRequest.value.data.vehicle_engine_name + ' - #' + clientRequest.value.data.id;
    } else {
        title = clientRequest.value.data.client.data.first_name + ' ' + clientRequest.value.data.client.data.last_name + ' - #' + clientRequest.value.data.id;
    }
    return title;
}

const idToDuplicate = ref(null);
function showModalDuplicate(id) {
    idToDuplicate.value = id;
    const modaleDuplicate = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalDuplicate'))
    modaleDuplicate.show()
}
function duplicateClientRequest() {
    axios.post('/api/client-requests/' + idToDuplicate.value + '/duplicate')
        .then(response => {
            const modaleDuplicate = bootstrap.Modal.getInstance(document.getElementById('modalDuplicate'))
            modaleDuplicate.hide()
            router.push({ name: 'client-requests.show', params:{ id: response.data.data.id } })
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

watch(() => router.currentRoute.value.params.id, (newId) => {
    getClientRequest(newId)
})

const loadingDeleteEvent = ref(false)
function deleteEvent() {
    loadingDeleteEvent.value = true;
    axios.delete("/api/events/" + clientRequest.value.data.appointment.data.id)
        .then(() => {
            clientRequest.value.data.appointment = null;
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalCalendar'))
            modal.hide();
            $toast.success('RDV supprimé', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            showAlertDeleteEvent.value = false;
            loadingDeleteEvent.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
            loadingDeleteEvent.value = false;
            showAlertDeleteEvent.value = false;
        });
}

const showAlertDeleteEvent = ref(false)

function mapCarStageToPrestation(carStage) {
    const carStageMap = {
        'Reprogrammation stage 0': ['STAGE_0'],
        'Reprogrammation stage 1': ['STAGE_1'],
        'Reprogrammation stage 2': ['STAGE_2'],
        'Reprogrammation stage 3': ['STAGE_3'],
        'Conversion E85': ['E85_FLEXFUEL'],
        'Conversion E85+': ['E85_FLEX+']
    };

    return carStageMap[carStage] || null;
}

function mapCarStageToGoogleColorId(carStage) {
    const carStageMapColor = {
        'Reprogrammation stage 0': 9,
        'Reprogrammation stage 1': 7,
        'Reprogrammation stage 2': 7,
        'Reprogrammation stage 3': 7,
        'Conversion E85': 2,
        'Conversion E85+': 6
    };

    return carStageMapColor[carStage] || null;
}
</script>

<template>
    <div class="row">
        <div class="col-12" v-show="loading">
            <loader />
        </div>
        <div class="col-xl-6" v-if="!loading">
            <div class="card border">
                <div class="card-header align-items-center d-flex">
                    <h4 class="card-title mb-0 flex-grow-1">
                        <i class="mdi me-2 align-middle fs-4" :class="router.currentRoute.value.name === 'client-request-others.show' ? 'mdi-folder-information-outline' : 'mdi-car-info'"></i>
                        {{ router.currentRoute.value.name === 'client-request-others.show' ? 'Autre demande' : 'Demande client' }}
                    </h4>
                    <div class="avatar-xs me-2" v-if="clientRequest.can.update">
                        <a href="#" class="avatar-title bg-secondary-subtle text-secondary fs-16 align-middle rounded" title="Dupliquer la demande" @click.prevent="showModalDuplicate(clientRequest.data.id)">
                            <i class="ri-file-copy-line"></i>
                        </a>
                    </div>
                    <div class="avatar-xs me-2" v-if="clientRequest.can.update">
                        <a href="#" class="avatar-title bg-success-subtle text-success fs-16 align-middle rounded" title="RDV Client" @click.prevent="showModalCalendar(event, 'modalCalendar');">
                            <i class="ri-calendar-check-fill"></i>
                        </a>
                    </div>
                    <div class="avatar-xs me-2" v-if="clientRequest.can.update">
                        <a href="#" class="avatar-title bg-dark-subtle text-dark fs-15 rounded" title="Fiche de travail" @click.prevent="showModalPdf">
                            <i class="ri-file-edit-fill"></i>
                        </a>
                    </div>
                    <div class="avatar-xs me-2">
                        <a href="#" class="avatar-title bg-warning-subtle text-warning fs-15 rounded" title="Notes" @click.prevent="showModalInternalNotes">
                            <i class="ri-sticky-note-fill"></i>
                        </a>
                    </div>
                    <div class="avatar-xs" v-if="clientRequest.can.update">
                        <router-link :to="{ name: 'client-requests.edit', params:{ id: clientRequest.data.id } }" class="avatar-title bg-info-subtle text-info fs-15 rounded" title="Modifier">
                            <i class="ri-edit-2-fill"></i>
                        </router-link>
                    </div>

                </div>
                <div class="card-body">

                    <div class="table-responsive">
                        <table class="table table-md table-nowrap mb-3">
                            <tbody>
                            <tr>
                                <th scope="row" class="col-5 border-top-0">ID</th>
                                <td class="border-top-0 text-body fw-semibold">#{{ clientRequest.data.id }} <copy-button :value="clientRequest.data.id" label="l'ID" /></td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Centre</th>
                                <td>{{ clientRequest.data.center.data.name }} <copy-button :value="clientRequest.data.center.data.name" label="le centre" /></td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Statut</th>
                                <td>
                                    <span :class="'fs-11 badge bg-'+clientRequest.data.status_badge_color+'-subtle  text-'+clientRequest.data.status_badge_color">{{ clientRequest.data.status_label_fr }}</span>
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info ms-1" id="statusDropdown" data-bs-toggle="dropdown" v-if="clientRequest.can.update"></a>
                                    <ul class="dropdown-menu" aria-labelledby="statusDropdown">
                                        <li v-for="(status, key) in clientRequestStatuses" :key="key">
                                            <a class="dropdown-item" href="#" @click.prevent="changeStatus(status.value)">
                                                <span :class="`fs-11 badge bg-${status.badgeColor}-subtle text-${status.badgeColor}`">{{ status.label }}</span>
                                            </a>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                            <tr v-if="clientRequest.data.status === 'lost'">
                                <th scope="row" class="col-5">Raison perte</th>
                                <td class="align-middle">
                                   {{ clientRequest.data.reason_for_loss }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                   <copy-button :value="clientRequest.data.reason_for_loss" label="la raison de perte" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Client</th>
                                <td class="align-middle">
                                    <router-link :to="{ name: 'clients.show', params: { id: clientRequest.data.client.data.id }}" class="mr-1 align-middle text-secondary" title="Afficher la fiche client">
                                        {{ clientRequest.data.client.data.last_name }} {{ clientRequest.data.client.data.first_name }}
                                    </router-link>
                                    <span :class="'fs-11 align-middle ms-2 badge bg-'+clientRequest.data.client.data.client_status_badge_color+'-subtle  text-'+clientRequest.data.client.data.client_status_badge_color">{{ clientRequest.data.client.data.client_status_label_fr }}</span>
                                    <router-link :to="{ name: 'clients.edit', params:{ id: clientRequest.data.client.data.id } }" class="text-info align-middle" title="Modifier le client" v-if="clientRequest.data.client.can.update">
                                        <i class="ri-pencil-fill fs-17 px-1 align-middle"></i>
                                    </router-link>
                                    <copy-button :value="clientRequest.data.client.data.last_name + ' ' + clientRequest.data.client.data.first_name" label="le client" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Téléphone</th>
                                <td>{{ clientRequest.data.client.data.phone_number }} <copy-button :value="clientRequest.data.client.data.phone_number" label="le téléphone" /></td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Email</th>
                                <td>
                                    {{ clientRequest.data.client.data.email }} <copy-button :value="clientRequest.data.client.data.email" label="l'email" />
                                    <span class="badge bg-danger-subtle text-danger ms-1" v-tooltip="clientRequest.data.client.data.email_invalid_reason" v-if="clientRequest.data.client.data.email_invalid_reason">
                                        <i class="ri-mail-close-line align-bottom me-1"></i>Invalide
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Raisons contact</th>
                                <td>
                                    {{ clientRequest.data.contact_reason }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.contact_reason" label="la raison de contact" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Prestation</th>
                                <td>
                                    {{ clientRequest.data.car_stage === null ? '--' : clientRequest.data.car_stage }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.car_stage" label="la prestation" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Véhicule - Type</th>
                                <td>
                                    {{ clientRequest.data.vehicle_type_label || clientRequest.data.vehicle_power_type_label
                                    ? [clientRequest.data.vehicle_type_label, clientRequest.data.vehicle_power_type_label].filter(Boolean).join(' - ')
                                    : '--' }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                </td>
                            </tr>
                            <tr v-if="clientRequest.data.vehicle_brand_name !== null || clientRequest.data.vehicle_model_name !== null">
                                <th scope="row" class="col-5">Marque - Modèle</th>
                                <td>
                                    {{ clientRequest.data.vehicle_brand_name }} - {{ clientRequest.data.vehicle_model_name }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.vehicle_brand_name + ' - ' + clientRequest.data.vehicle_model_name" label="la marque et le modèle" />
                                </td>
                            </tr>
                            <tr v-if="clientRequest.data.vehicle_version_name !== null || clientRequest.data.vehicle_engine_name !== null">
                                <th scope="row" class="col-5 align-middle">Version - Moteur</th>
                                <td>
                                    <span class="align-middle">{{ clientRequest.data.vehicle_version_name }} - {{ clientRequest.data.vehicle_engine_name }}</span>
                                    <router-link :to="{ name: 'chiptuning.show', params:{ id: clientRequest.data.engine.data.id } }" target="_blank" class="ms-2 btn btn-sm btn-dark bg-gradient bg-gradient waves-effect waves-light btn-sm" v-if="clientRequest.data.engine !== null">
                                        Fiche moteur
                                    </router-link>
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">VIN</th>
                                <td>
                                    {{ clientRequest.data.vin ?? '--' }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.vin" label="le VIN" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Immatriculation</th>
                                <td>
                                    {{ clientRequest.data.license_plate ?? '--' }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.license_plate" label="l'immatriculation" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Date de 1ere immatriculation</th>
                                <td>
                                    {{ clientRequest.data.vehicle_year_fr ?? '--' }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.vehicle_year_fr" label="la date de 1ère immatriculation" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Kilométrage</th>
                                <td>
                                    {{ clientRequest.data.mileage_formatted === null ? '--' : clientRequest.data.mileage_formatted+ ' km' }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.mileage_formatted" label="le kilométrage" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Prix</th>
                                <td>
                                    {{ clientRequest.data.price_formatted === null ? '--' : clientRequest.data.price_formatted + ' €' }}
                                    <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    <copy-button :value="clientRequest.data.price_formatted" label="le prix" />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5">Tags</th>
                                <td>
                                    <div class="d-flex align-items-center flex-wrap gap-2">
                                        <span v-for="(tag, tagIndex) in clientRequest.data.tags" :key="tagIndex" class="badge bg-primary-subtle text-primary fs-11 my-badge">{{ tag }}</span>
                                        <a href="#" class="ri-pencil-fill fs-17 text-info" @click.prevent="showModalClientRequestUpdate" v-if="clientRequest.can.update"></a>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="clientRequest.data.website_page !== null">
                                <th scope="row" class="col-5">Dernière page visitée</th>
                                <td><a :href="clientRequest.data.website_page" target="_blank" class="text-secondary">Lien site web</a></td>
                            </tr>
                            <tr>
                                <th scope="row" class="col-5 border-bottom-0">Date création</th>
                                <td class="border-bottom-0">{{ clientRequest.data.created_at_fr }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="alert alert-success alert-label-icon rounded-label shadow border" role="alert" v-if="clientRequest.data.appointment !== null">
                            <i class="ri-calendar-check-fill label-icon"></i>
                        <strong>Rendez-vous :</strong><br>
                        <p class="mb-0">
                            Le {{ clientRequest.data.appointment.data.start_end_date_fr }} à {{ clientRequest.data.appointment.data.start_end_hour_fr }}<br>
                        </p>
                    </div>
                    <div class="alert alert-secondary alert-label-icon rounded-label shadow border" role="alert">
                        <i class="ri-chat-quote-line label-icon"></i>
                        <strong>Commentaire client : </strong><br>
                        <p v-html="clientRequest.data.client_comment !== null ? clientRequest.data.client_comment.replace(/\n/g, '<br />') : 'Aucun commentaire.'" class="mb-0"></p>
                    </div>
                    <div class="alert alert-warning alert-label-icon rounded-label shadow" role="alert" >
                        <a href="#" class="ri-sticky-note-fill label-icon" @click.prevent="showModalInternalNotes"></a>
                        <strong>Notes internes : </strong><br>
                        <p  v-if="clientRequest.data.notes !== null" v-html="clientRequest.data.notes.replace(/\n/g, '<br />')" class="mb-0"></p>
                        <p  class="mb-0" v-else>--</p>
                    </div>

                </div>
            </div>
            <div class="card" v-if="clientRequest.data.attachments.length > 0">
                <div class="card-header align-items-center d-flex border-bottom-dashed">
                    <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-attachment me-2 align-middle fs-4"></i>Fichiers joints</h4>
                </div>

                <div class="card-body">

                    <attachment-files :attachments="clientRequest.data.attachments" stacked downloadable />
                </div>
            </div>
            <!-- Modal notes -->
            <div id="modalInternalNotes" class="modal fade" tabindex="-1" aria-labelledby="modalInternalNotes" aria-hidden="true" style="display: none;">
                <div class="modal-dialog modal-lg">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="modalInternalNotes">Notes internes</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"> </button>
                        </div>
                        <div class="modal-body">
                            <form-textarea
                                name="notes"
                                label="Notes internes"
                                :label-hidden="false"
                                @update:field="internalNotes = $event"
                                :errors="errors"
                                :required="false"
                                :data="internalNotes"
                                bs-class="col-xl-12"
                                :row="10"
                            />
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                            <button type="button" class="btn btn-secondary" @click.prevent="updateInternalNotes">Valider</button>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Modal lost reason -->
            <div id="modalReasonForLoss" class="modal fade" tabindex="-1" aria-labelledby="modalReasonForLoss" aria-hidden="true" style="display: none;">
                <div class="modal-dialog modal-lg">
                    <loader v-if="loadingReasonForLoss"></loader>
                    <form v-on:submit.prevent="updateReasonForLoss" v-show="!loadingReasonForLoss">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="modalReasonForLoss">Raison de la perte</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"> </button>
                        </div>
                        <div class="modal-body">
                            <form-select-field
                                name="reason_for_loss"
                                label="Raison de la perte"
                                :label-hidden="false"
                                @update:field="formReasonForLoss.reason_for_loss = $event"
                                :errors="errors"
                                :required="formReasonForLoss.status === 'lost'"
                                :data="formReasonForLoss.reason_for_loss"
                                bs-class="col-xl-12"
                                :disabled="false"
                                :liste-options="[
                                    { text: 'Doublon', value: 'Doublon' },
                                    { text: 'Plus de réponse', value: 'Plus de réponse' },
                                    { text: 'Prix', value: 'Prix' },
                                    { text: 'Concurrent', value: 'Concurrent' },
                                    { text: 'Délais', value: 'Délais' },
                                    { text: 'Perte de confiance', value: 'Perte de confiance' },
                                    { text: 'Hors sujet', value: 'Hors sujet' },
                                    { text: 'Garantie', value: 'Garantie' },
                                    { text: 'Assurance', value: 'Assurance' },
                                ]"
                            />
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                            <button type="submit" class="btn btn-secondary" >Valider</button>
                        </div>
                    </div>
                    </form>
                </div>
            </div>
            <!-- Modal fiche travail -->
            <div id="modalPdf" class="modal fade" tabindex="-1" aria-labelledby="modalPdf" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false" style="display: none;">
                <div class="modal-dialog modal-lg">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="modalPdf">Fiche de travail</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"> </button>
                        </div>
                        <div class="modal-body">
                            <!--Missing data-->
                            <div class="alert alert-danger alert-border-left alert-dismissible fade shadow show mb-3" role="alert" v-if="canGeneratePdf() !== true">
                                <p>
                                    Afin de générer la fiche de travail, l'ensemble des informations suivantes doivent être renseignées dans la demande client :
                                </p>
                                <ul>
                                    <li v-if="clientRequest.data.vehicle_brand_id === null">Sélection de la marque dans le configurateur</li>
                                    <li v-if="clientRequest.data.vehicle_model_id === null">Sélection du modèle dans le configurateur</li>
                                    <li v-if="clientRequest.data.vehicle_version_id === null">Sélection de la version dans le configurateur</li>
                                    <li v-if="clientRequest.data.vehicle_engine_id === null">Sélection du moteur dans le configurateur</li>
                                    <li v-if="clientRequest.data.vin === null">VIN</li>
                                    <li v-if="clientRequest.data.license_plate === null">Immatriculation</li>
                                    <li v-if="clientRequest.data.vehicle_year === null">Date de première immatriculation</li>
                                    <li v-if="clientRequest.data.mileage === null">Kilométrage</li>
                                    <li v-if="clientRequest.data.price === null">Prix</li>
                                </ul>
                                <div v-if="clientRequest.data.vehicle_power_type === null || clientRequest.data.vehicle_power_type === 'petrol' || clientRequest.data.vehicle_power_type === 'hybrid_petrol'">
                                    <p>
                                        Pour les véhicules à essence :
                                    </p>
                                    <ul class="mb-3">
                                        <li>Kilométrage bougies</li>
                                        <li>Kilométrage bobines</li>
                                        <li>Indice octane</li>
                                    </ul>
                                </div>
                                <router-link :to="{ name: 'client-requests.edit', params:{ id: clientRequest.data.id } }" type="button" class="btn btn-sm btn-dark bg-gradient waves-effect waves-light">Modifier la demande</router-link>
                            </div>
                            <!--Generated document-->
                            <div class="alert alert-info shadow" role="alert" v-if="clientRequest.data.worksheet_path !== null && clientRequest.data.worksheet_signed_path === null">
                                <p class="mb-0">
                                    Vous avez déjà généré une fiche de travail le <strong>{{ clientRequest.data.worksheet_generated_at }}</strong>.
                                    <br>Vous pouvez l'afficher en cliquant sur le bouton ci-dessous : <br>
                                    <a :href="clientRequest.data.worksheet_path" target="_blank" class="btn btn-sm btn-info bg-gradient waves-effect waves-light mt-2 align-middle me-2">
                                        <i class="mdi mdi-file-certificate-outline align-middle me-1 fs-12"></i>Afficher
                                    </a>
                                    <a href="#" class="btn btn-sm btn-dark bg-gradient waves-effect waves-light mt-2 align-middle" v-if="!loadingSignature" @click.prevent="generateSubmission()">
                                        Signer le document
                                    </a>
                                    <button type="button" class="btn btn-sm btn-dark btn-load bg-gradient mt-2 align-middle" v-if="loadingSignature">
                                        <span class="d-flex align-items-center">
                                            <span class="flex-grow-1 me-2">
                                                Préparation...
                                            </span>
                                            <span class="spinner-border flex-shrink-0" role="status">
                                                <span class="visually-hidden">Préparation...</span>
                                            </span>
                                        </span>
                                    </button>
                                </p>
                            </div>
                            <!--Signed document and audit-->
                            <div class="alert alert-success shadow" role="alert" v-if="clientRequest.data.worksheet_signed_path !== null">
                                <p class="mb-0">
                                    La fiche de travail est signée.
                                    <br>Vous pouvez l'afficher ainsi que l'audit en cliquant sur l'un des bouton ci-dessous : <br>
                                    <a :href="clientRequest.data.worksheet_signed_path" target="_blank" class="btn btn-sm btn-dark bg-gradient waves-effect waves-light mt-2 align-middle me-2">
                                        <i class="mdi mdi-file-certificate-outline align-middle me-1 fs-12"></i>Fiche signée
                                    </a>
                                    <a :href="clientRequest.data.worksheet_audit_path" target="_blank" class="btn btn-sm btn-dark bg-gradient waves-effect waves-light mt-2 align-middle me-2">
                                        <i class="mdi mdi-file-certificate-outline align-middle me-1 fs-12"></i>Audit
                                    </a>
                                </p>
                            </div>
                            <!--Origin reset-->
                            <div class="alert alert-warning shadow" role="alert" v-if="showOriginReset">
                                <p class="mb-0">
                                    Si vous effectuez une remise à l'origine configurée par un autre préparateur, veuillez procéder en mode bench ou boot afin d'éviter tout risque de plantage dû à un possible immo OFF.
                                </p>
                            </div>
                            <!--Form-->
                            <div v-if="clientRequest.data.worksheet_signed_path === null && !generatingPdf">
                                <form-multi-select
                                    :errors="errors"
                                    label="Prestations"
                                    name="prestations"
                                    :required="true"
                                    :data="formPdf.prestations"
                                    @update:field="formPdf.prestations = $event"
                                    bs-class="col-md-12"
                                    :disabled="false"
                                    :label-hidden="false"
                                    placeholder="Sélectionnez une ou plusieurs prestations"
                                    :options-pre-selected="prestationPreSelected"
                                    :liste-options="[
                                        { name: 'Stage 0', id: 'STAGE_0' },
                                        { name: 'Stage 1', id: 'STAGE_1' },
                                        { name: 'Stage 2', id: 'STAGE_2' },
                                        { name: 'Stage 3', id: 'STAGE_3' },
                                        { name: 'E85 Flex+', id: 'E85_FLEX+' },
                                        { name: 'E85 Flexfuel', id: 'E85_FLEXFUEL' },
                                        { name: 'Remise d\'origine', id: 'ORIGIN_RESET' },
                                        { name: 'Banc de puissance', id: 'POWER_BENCH' },
                                        { name: 'Boite de vitesse', id: 'GEARBOX' },
                                        { name: 'Entretien', id: 'ENTRETIEN' },
                                        { name: 'Réparation, Pièces performance et Accessoires', id: 'REPARATION_ACCESSOIRES' },
                                        { name: 'SAV', id: 'SAV' },
                                    ]"
                                />

                                <form-multi-select
                                    :errors="errors"
                                    label="Options"
                                    name="options"
                                    :required="false"
                                    :data="formPdf.options"
                                    @update:field="formPdf.options = $event"
                                    bs-class="col-md-12"
                                    :disabled="false"
                                    :label-hidden="false"
                                    placeholder="Sélectionnez une ou plusieurs options"
                                    :liste-options="[
                                        { name: 'Diagnostic Électronique et Mise à Jour du Système AdBlue', id: 'SCR_OFF' },
                                        { name: 'Diagnostic Électronique et Mise à Jour du Système Catalytique', id: 'DECAT_CAT_OFF' },
                                        { name: 'Diagnostic Électronique et Mise à Jour du Système EGR', id: 'EGR_OFF' },
                                        { name: 'Diagnostic Électronique et Mise à Jour du Système FAP', id: 'DPF_OFF' },
                                        { name: 'Diagnostic Électronique et Mise à Jour du Système GPF', id: 'GPF_OFF' },
                                        { name: 'Entretien Forfait ESSENTIEL', id: 'ESSENTIAL_PLAN' },
                                        { name: 'Entretien Forfait SÉRÉNITÉ', id: 'CHILL_PLAN' },
                                        { name: 'Esthétique', id: 'ESTHETIQUE' },
                                        { name: 'Option Clapets d\'Admission', id: 'SWIRL_FLAP_OFF' },
                                        { name: 'Option Cold Start', id: 'COLD_START_OFF' },
                                        { name: 'Option Covering', id: 'COVERING' },
                                        { name: 'Option Désactivation des Cylindres', id: 'CYLINDER_ON_DEMAND_OFF' },
                                        { name: 'Option Detailing', id: 'DETAILING' },
                                        { name: 'Option DSG Fart', id: 'DSG_FART' },
                                        { name: 'Option DTC', id: 'DTC_OFF' },
                                        { name: 'Option Exhaust Flaps', id: 'EXHAUST_FLAPS' },
                                        { name: 'Option Forfait Vidange Boîte de Vitesses Automatique', id: 'VIDANGE_BOITE' },
                                        { name: 'Option Hard Rev Cut', id: 'HARD_REV_CUT' },
                                        { name: 'Option Launch Control', id: 'LAUNCH_CONTROL' },
                                        { name: 'Option Pop & Bang', id: 'POP_BANG' },
                                        { name: 'Option Pop & Bang Bouton', id: 'POP_BANG_START_BUTTON' },
                                        { name: 'Option Pop Corn', id: 'POP_CORN' },
                                        { name: 'Option PPF – Film de Protection Carrosserie', id: 'PPF' },
                                        { name: 'Option Protection Céramique', id: 'CERAMIC' },
                                        { name: 'Option Sport Display', id: 'SPORT_DISPLAY' },
                                        { name: 'Option Start & Stop', id: 'START_STOP_OFF' },
                                        { name: 'Option VMax', id: 'VMAX_OFF' },
                                        { name: 'Option Vitres Teintées', id: 'VITRE_TINTEES' },
                                    ]"
                                />
                            </div>

                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                            <button type="button" class="btn btn-secondary" @click.prevent="generatePdf" :disabled="!canGeneratePdf()" v-if="!generatingPdf && clientRequest.data.worksheet_signed_path === null">{{ clientRequest.data.worksheet_generated_at !== null ? 'Regénérer' : 'Générer' }}</button>
                            <button type="button" class="btn btn-secondary btn-load"  v-if="generatingPdf && clientRequest.data.worksheet_signed_path === null">
                                <span class="d-flex align-items-center">
                                    <span class="flex-grow-1 me-2">
                                        Génération...
                                    </span>
                                    <span class="spinner-border flex-shrink-0" role="status">
                                        <span class="visually-hidden">Génération...</span>
                                    </span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Modal Signature -->
            <div id="modalSignature" class="modal fade" tabindex="-1" aria-labelledby="modalSignature" aria-hidden="true" style="display: none;" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-xl">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="modalSignature">Signer le document</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"> </button>
                        </div>
                        <div class="modal-body">
                            <DocusealForm
                                :with-title="false"
                                :with-send-copy-button="false"
                                :with-download-button="false"
                                @complete="signatureCompleted"
                                language="fr"
                                v-if="slugForm !== null"
                                :src="`https://docuseal.co/s/${slugForm}`"
                            />
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Modal calendar -->
            <div id="modalCalendar" class="modal fade" aria-labelledby="modalCalendar" aria-hidden="true" style="display: none;" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-xl">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="modalCalendar">Fixer le RDV client</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"> </button>
                        </div>
                        <div class="modal-body">
                            <form @submit.prevent="setAppointmentDate">
                                <!-- Très grand écran : une ligne ; sinon début, fin et type puis titre et boutons. Les champs ont déjà leur marge du bas. -->
                                <div class="row gx-3">
                                    <form-date-time-picker
                                        name="start_date"
                                        label="Début"
                                        :label-hidden="false"
                                        @update:field="formAppointment.start_date = $event"
                                        :errors="errors"
                                        :required="true"
                                        :data="formAppointment.start_date"
                                        bs-class="col-md-4 col-xxl-2"
                                        :disabled="false"
                                    />

                                    <form-date-time-picker
                                        name="end_date"
                                        label="Fin"
                                        :label-hidden="false"
                                        @update:field="formAppointment.end_date = $event"
                                        :errors="errors"
                                        :required="true"
                                        :data="formAppointment.end_date"
                                        bs-class="col-md-4 col-xxl-2"
                                        :disabled="formAppointment.start_date === null"
                                    />

                                    <google-calendar-color-selector
                                        name="google_color_id"
                                        label="Type de RDV"
                                        :color-pre-selected="preselectedColor"
                                        @update:field="formAppointment.google_color_id = $event.id"
                                        :required="true"
                                        bs-class="col-md-4 col-xxl-2"
                                        :errors="errors"
                                    />

                                    <form-text-field
                                        name="title"
                                        label="Titre du RDV"
                                        :data="formAppointment.title"
                                        @update:field="formAppointment.title = $event"
                                        :placeholder="getEventTitle()"
                                        :errors="errors"
                                        bs-class="col-md"
                                    />

                                    <!-- Largeur naturelle des boutons : le titre prend la place restante -->
                                    <div class="col-md-auto d-flex gap-2 justify-content-end align-items-end mb-3 text-nowrap">
                                        <!--Button delete event-->
                                        <button type="button" class="btn btn-soft-danger btn-icon" v-tooltip="'Supprimer le RDV'" @click.prevent="showAlertDeleteEvent = true" v-if="clientRequest.data.appointment !== null && !showAlertDeleteEvent"><i class="ri-delete-bin-line"></i></button>
                                        <!--Button deleting event-->
                                        <button type="button" class="btn btn-soft-dark" @click.prevent="showAlertDeleteEvent = false" v-if="clientRequest.data.appointment !== null && showAlertDeleteEvent">Annuler</button>
                                        <!--Submit-->
                                        <button type="button" class="btn btn-success btn-load" disabled v-if="loadingSetAppointment">
                                            <span class="d-flex align-items-center">
                                                <span class="spinner-border flex-shrink-0" role="status"></span>
                                                <span class="flex-grow-1 ms-2">Enregistrement...</span>
                                            </span>
                                        </button>
                                        <button type="submit" class="btn btn-success" v-else>
                                            <i class="ri-calendar-check-line align-middle me-1"></i>Valider le RDV
                                        </button>
                                    </div>
                                </div>

                                <!-- Type de RDV différent de la prestation de la demande -->
                                <div class="bg-light rounded px-3 py-2 mb-3" v-if="prestationMismatch">
                                    <p class="text-muted mb-2">
                                        <i class="ri-error-warning-line text-warning align-middle me-1"></i>
                                        La demande indique <span class="fw-medium text-body">« {{ clientRequest.data.car_stage ?? 'aucune prestation' }} »</span>, le RDV est de type <span class="fw-medium text-body">« {{ getAppointmentTypeLabel(formAppointment.google_color_id) }} »</span>. La prestation est reprise dans l'e-mail de confirmation HubSpot.
                                    </p>
                                    <div class="row g-3 align-items-center">
                                        <form-select-field
                                            name="appointment_car_stage"
                                            label="Nouvelle prestation"
                                            :label-hidden="true"
                                            :errors="errors"
                                            :data="prestationUpdate.value"
                                            @update:field="prestationUpdate.value = $event"
                                            :liste-options="PRESTATION_SELECT_OPTIONS"
                                            :disabled="!prestationUpdate.enabled"
                                            bs-class="col-md-5"
                                            margin="mb-0"
                                        />
                                        <form-switch
                                            name="update_car_stage"
                                            label="Mettre à jour la prestation de la demande"
                                            :errors="errors"
                                            :data="prestationUpdate.enabled"
                                            @update:field="prestationUpdate.enabled = $event"
                                            bs-class="col-md-7"
                                            margin="mb-0"
                                        />
                                    </div>
                                </div>

                            </form>
                            <div class="alert alert-danger alert-dismissible alert-label-icon rounded-label shadow fade show mb-3 d-flex align-items-center justify-content-between pe-3" role="alert" v-if="showAlertDeleteEvent">
                                <div>
                                    <i class="ri-delete-bin-line label-icon"></i><strong>Suppression</strong>
                                    - Confirmer la suppression du RDV ?
                                </div>
                                <div>
                                    <!--Button delete event-->
                                    <button type="button" class="btn btn-danger" v-if="clientRequest.data.appointment !== null && !loadingDeleteEvent" @click.prevent="deleteEvent">Supprimer</button>
                                    <!--Button deleting event-->
                                    <button type="button" class="btn btn-danger btn-load" v-if="clientRequest.data.appointment !== null && loadingDeleteEvent">
                                    <span class="d-flex align-items-center">
                                        <span class="spinner-border flex-shrink-0" role="status"></span>
                                        <span class="flex-grow-1 ms-2">
                                            Suppression...
                                        </span>
                                    </span>
                                    </button>
                                </div>
                            </div>
                            <p class="text-muted fs-12 mb-2">
                                <i class="ri-information-line align-middle me-1"></i>Cliquez sur un créneau ou faites glisser la souris sur plusieurs créneaux pour choisir l'horaire. Le RDV se déplace et s'allonge aussi directement dans l'agenda.
                            </p>
                            <div class="modal-calendar" :class="{ 'has-current-appointment': formAppointment.start_date && formAppointment.end_date }" :style="{ '--current-appointment-color': getColorFromGoogleCalendarId(formAppointment.google_color_id) }">
                                <FullCalendar :options='calendarOptions' ref="calendarRef" v-if="displayCalendar"/>
                            </div>
                            <loader v-if="loadingCalendar"/>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                        </div>
                    </div>
                </div>
            </div>
            <!--Modal update client request-->
            <div class="modal fade zoomIn" id="modalUpdateClientRequest" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-lg">
                    <div class="modal-content" >
                        <div class="modal-header">
                            <h5 class="modal-title">
                                Modifier demande client
                            </h5>
                        </div>
                        <div class="modal-body" v-if="loadingClientRequestUpdate">
                            <loader/>
                        </div>
                        <form v-on:submit.prevent="updateClientRequest" v-if="!loadingClientRequestUpdate">
                            <div class="modal-body row">

                                <form-select-field
                                    name="status_request"
                                    label="Statut"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.status = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.status"
                                    bs-class="col-xl-6"
                                    :liste-options="[
                                        { text: 'À traiter', value: 'pending' },
                                        { text: 'Répondu', value: 'responded' },
                                        { text: 'Pièces commandées', value: 'parts_ordered' },
                                        { text: 'RDV programmé', value: 'appointment_scheduled' },
                                        { text: 'RDV en cours', value: 'appointment_ongoing' },
                                        { text: 'Gagné', value: 'won' },
                                        { text: 'Client non présenté', value: 'no_show' },
                                        { text: 'Perdu', value: 'lost' },
                                        { text: 'Fermé', value: 'closed' }
                                    ]"
                                />

                                <form-select-field
                                    name="request_origin"
                                    label="Origine demande"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.request_origin = $event"
                                    :errors="errors"
                                    :required="true"
                                    :data="formClientRequestUpdate.request_origin"
                                    bs-class="col-xl-6"
                                    :disabled="false"
                                    :liste-options="[
                                        { text: 'Comptoir', value: 'Comptoir' },
                                        { text: 'Événement', value: 'Événement' },
                                        { text: 'Influenceur', value: 'Influenceur' },
                                        { text: 'Réseaux sociaux', value: 'Réseaux sociaux' },
                                        { text: 'RingOver', value: 'RingOver' },
                                        { text: 'ADS', value: 'ADS' },
                                        { text: 'Site web', value: 'Site web' },
                                        { text: 'Site web - ChatBot', value: 'Site web - ChatBot' },
                                        { text: 'Campagne SMS', value: 'Campagne SMS' },
                                        { text: 'Campagne Mail', value: 'Campagne Mail' },
                                        { text: 'Parrainage', value: 'Parrainage' },
                                        { text: 'Landing page', value: 'Landing page' },
                                    ]"
                                />

                                <form-select-field
                                    v-show="formClientRequestUpdate.status === 'lost'"
                                    name="reason_for_loss"
                                    label="Raison de la perte"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.reason_for_loss = $event"
                                    :errors="errors"
                                    :required="formClientRequestUpdate.status === 'lost'"
                                    :data="formClientRequestUpdate.reason_for_loss"
                                    bs-class="col-xl-12"
                                    :disabled="false"
                                    :liste-options="[
                                        { text: 'Doublon', value: 'Doublon' },
                                        { text: 'Plus de réponse', value: 'Plus de réponse' },
                                        { text: 'Prix', value: 'Prix' },
                                        { text: 'Concurrent', value: 'Concurrent' },
                                        { text: 'Délais', value: 'Délais' },
                                        { text: 'Perte de confiance', value: 'Perte de confiance' },
                                        { text: 'Hors sujet', value: 'Hors sujet' },
                                        { text: 'Garantie', value: 'Garantie' },
                                        { text: 'Assurance', value: 'Assurance' },
                                    ]"
                                />

                                <form-select-field
                                    name="contact_reason"
                                    label="Raison contact"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.contact_reason = $event"
                                    :errors="errors"
                                    :required="true"
                                    :data="formClientRequestUpdate.contact_reason"
                                    bs-class="col-xl-6"
                                    :liste-options="[
                                        { text: 'Rendez-vous', value: 'Rendez-vous' },
                                        { text: 'Demande de rappel', value: 'Demande de rappel' },
                                        { text: 'Devis', value: 'Devis' },
                                        { text: 'Demande d’information', value: 'Demande d’information' },
                                        { text: 'Devenir franchisé', value: 'Devenir franchisé' },
                                        { text: 'Reprog. en Attente', value: 'Reprog. en Attente' },
                                        { text: 'Participer au Dev.', value: 'Participer au Dev.' },
                                        { text: 'Assistance', value: 'Assistance' },
                                        { text: 'JOBS', value: 'JOBS' },
                                        { text: 'Tournage vidéo Youtube', value: 'Tournage vidéo Youtube' },
                                        { text: 'Autre', value: 'Autre' }
                                    ]"
                                />

                                <form-select-field
                                    name="car_stage"
                                    label="Prestation souhaitée"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.car_stage = $event"
                                    :errors="errors"
                                    :required="!userIsAdmin() && !userIsBackOffice()"
                                    :data="formClientRequestUpdate.car_stage"
                                    bs-class="col-xl-6"
                                    :liste-options="[
                                            { text: 'Reprogrammation stage 0', value: 'Reprogrammation stage 0' },
                                            { text: 'Reprogrammation stage 1', value: 'Reprogrammation stage 1' },
                                            { text: 'Reprogrammation stage 2', value: 'Reprogrammation stage 2' },
                                            { text: 'Reprogrammation stage 3', value: 'Reprogrammation stage 3' },
                                            { text: 'Boîte de vitesses', value: 'Boîte de vitesses' },
                                            { text: 'Conversion E85', value: 'Conversion E85' },
                                            { text: 'Conversion E85+', value: 'Conversion E85+' },
                                            { text: 'Pièces performance', value: 'Pièces performance' },
                                            { text: 'Entretien', value: 'Entretien' },
                                            { text: 'Passage au banc', value: 'Passage au banc' },
                                            { text: 'Esthétique', value: 'Esthétique' },
                                            { text: 'Diagnostic', value: 'Diagnostic' },
                                        ]"
                                />

                                <form-multi-select
                                    :errors="errors"
                                    label="Tags"
                                    name="tags"
                                    :required="false"
                                    :data="formClientRequestUpdate.tags"
                                    @update:field="formClientRequestUpdate.tags = $event"
                                    bs-class="col-md-12"
                                    :disabled="false"
                                    :label-hidden="false"
                                    placeholder="Sélectionnez un ou plusieurs tags"
                                    :options-pre-selected="tagsPreSelected"
                                    :liste-options="[
                                            { name: 'Promotion', id: 'Promotion' },
                                            { name: 'ECU en dev', id: 'ECU en dev' },
                                            { name: 'SAV', id: 'SAV' },
                                            { name: 'Remise Prépa', id: 'Remise Prépa' },
                                            { name: 'Remise origine', id: 'Remise origine' },
                                            { name: 'Franchise', id: 'Franchise' },
                                            { name: 'Jobs', id: 'Jobs' },
                                            { name: 'Jeux-concours', id: 'Jeux-concours' },
                                            { name: 'Doublon', id: 'Doublon' },
                                            { name: 'Tournage vidéo Youtube', id: 'Tournage vidéo Youtube' }
                                        ]"
                                />

                                <form-select-field
                                    name="vehicle_type"
                                    label="Type véhicule"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.vehicle_type = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.vehicle_type"
                                    bs-class="col-xl-6"
                                    :disabled="false"
                                    :placeholder="formClientRequestUpdate.vehicle_type === null ? 'Sélectionnez un type'  : null"
                                    :liste-options="[
                                            { text: 'Voiture', value: 'car' },
                                            { text: 'Agricole', value: 'agricultural' },
                                            { text: 'ATV', value: 'atv' },
                                            { text: 'Camion', value: 'truck' },
                                            { text: 'Jetski', value: 'jetski' },
                                            { text: 'Moto', value: 'moto' },
                                            { text: 'Réinitialiser configurateur', value: null },
                                        ]"
                                />

                                <form-select-field
                                    name="vehicle_brand_id"
                                    label="Marques"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.vehicle_brand_id = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.vehicle_brand_id"
                                    bs-class="col-xl-6"
                                    :liste-options="carBrands"
                                    :placeholder="formClientRequestUpdate.vehicle_type === null ? 'Sélectionnez un type' : 'Toutes les marques'"
                                    :disabled="formClientRequestUpdate.vehicle_type === null"
                                />

                                <form-select-field
                                    name="vehicle_model_id"
                                    label="Modèles"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.vehicle_model_id = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.vehicle_model_id"
                                    bs-class="col-xl-6"
                                    :disabled="formClientRequestUpdate.vehicle_brand_id === null"
                                    :liste-options="carModels"
                                    :placeholder="formClientRequestUpdate.vehicle_brand_id === null ? 'Sélectionnez une marque' : 'Tous les modèles'"
                                />

                                <form-select-field
                                    name="vehicle_version_id"
                                    label="Versions"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.vehicle_version_id = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.vehicle_version_id"
                                    bs-class="col-xl-6"
                                    :disabled="formClientRequestUpdate.vehicle_model_id === null"
                                    :liste-options="carVersions"
                                    :placeholder="formClientRequestUpdate.vehicle_model_id === null ? 'Sélectionnez un modèle' : 'Toutes les versions'"
                                />

                                <form-select-field
                                    name="vehicle_engine_id"
                                    label="Motorisation"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.vehicle_engine_id = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.vehicle_engine_id"
                                    bs-class="col-xl-12"
                                    :disabled="formClientRequestUpdate.vehicle_version_id === null"
                                    :liste-options="carEngines"
                                    :placeholder="formClientRequestUpdate.vehicle_engine_id === null ? 'Sélectionnez un moteur' : 'Tous les moteurs'"
                                    :margin="formClientRequestUpdate.vehicle_engine_id === null ? 'mb-3' : 'mb-2'"
                                />

                                <form-text-field
                                    name="vin"
                                    label="VIN"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.vin = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.vin"
                                    bs-class="col-md-6"
                                    :show-char-count="true"
                                    :max-length="17"
                                />

                                <form-text-field
                                    name="license_plate"
                                    label="Immatriculation"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.license_plate = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.license_plate"
                                    bs-class="col-md-6"
                                />

                                <form-date-picker
                                    name="vehicle_year"
                                    label="Date de première immatriculation"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.vehicle_year = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.vehicle_year"
                                    bs-class="col-md-6"
                                    :disabled="false"
                                />

                                <form-numeric-field
                                    name="mileage"
                                    label="Kilométrage"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.mileage = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.mileage"
                                    bs-class="col-md-6"
                                    :step="1"
                                    :min="0"
                                />

                                <form-numeric-field
                                    name="price"
                                    label="Prix"
                                    :label-hidden="false"
                                    @update:field="formClientRequestUpdate.price = $event"
                                    :errors="errors"
                                    :required="false"
                                    :data="formClientRequestUpdate.price"
                                    bs-class="col-md-12"
                                    :min="0"
                                    :step="0.01"
                                />

                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn btn-soft-dark" data-bs-dismiss="modal">Fermer</button>
                                <button type="submit" class="btn btn-secondary">Valider</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <!--Modal duplicate-->
            <modal id="modalDuplicate" title="Dupliquer la demande ?" btn-class="btn-secondary" btn-text="Dupliquer" @click:button="duplicateClientRequest">
                <template v-slot:icon>
                    <lord-icon
                        src="https://cdn.lordicon.com/uecgmesg.json"
                        trigger="loop"
                        colors="primary:#212529,secondary:#3080e8"
                        style="width:90px;height:90px">
                    </lord-icon>
                </template>
                <template v-slot:content>
                    <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de dupliquer la demande client <strong>#{{ idToDuplicate }}</strong>. Confirmer la duplication ?</p>
                </template>
            </modal>
        </div>
        <div class="col-xl-6" v-if="!loading">
            <Chat
                :can-answer="(!userIsStaff() && !userIsExterne()) && !!clientRequest.data.client.data.email"
                :messageable-id="parseInt(clientRequest.data.id)"
                messageable-type="App\Models\ClientRequest"
                :fit-viewport="true"
                :show-is-internal="false"
                :show-default-messages="!userIsStaff() && !userIsExterne()"
                :default-message-center-id="parseInt(clientRequest.data.center_id)"
                :recipient-email-problem="clientRequest.data.client.data.email_invalid_reason"
                @recipient-email-invalid="clientRequest.data.client.data.email_invalid_reason = $event"
            />
        </div>
    </div>
</template>

<style scoped>
.modal-calendar{
    height: 70vh !important;
}
/* RDV en cours et zone sélectionnée : couleur du type choisi, au-dessus des autres RDV */
.modal-calendar :deep(.fc-current-appointment),
.modal-calendar :deep(.fc-event-mirror) {
    background-color: var(--current-appointment-color) !important;
    box-shadow: 0 0 0 2px #fff !important;
}
.modal-calendar :deep(.fc-timegrid-event-harness:has(.fc-current-appointment)) {
    z-index: 10 !important;
}
/* Les autres RDV passent au second plan quand le RDV en cours est placé */
.modal-calendar.has-current-appointment :deep(.fc-timegrid-event:not(.fc-current-appointment):not(.fc-event-mirror)) {
    opacity: 0.55;
}
@media (min-width: 576px) {
    .modal-xl {
        --vz-modal-width: 90% !important;
    }
}
</style>
